import React, { useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { RecurringContractDA } from '../../services/admin/recurring.contract.service';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { modalHide } from '../../utils/modal.util';
import { legendValid, legendInvalid } from '../../utils/legend.data.util';
import { formatMoney } from '../../scripts/format.value.script';

import AppInputTreasuryAccount from '../element/InputTreasuryAccount';
import AppLegend from '../element/Legend';

export interface ModalDepositReturnProps {
  companyForUser: any;
  treasuryAccountsForCompany: TreasuryAccount[];
  contract: any;
  setContract: React.Dispatch<React.SetStateAction<any>>;
}

let errorResponse: Error;

const AppModalDepositReturn: React.FunctionComponent<ModalDepositReturnProps> = ({companyForUser, treasuryAccountsForCompany, contract, setContract}) => {
  const {lang} = StateLanguage()

  const [retainedAmount, setRetainedAmount] = useState<string>('0')
  const [date, setDate] = useState<string>(new Date().toISOString().substring(0, 10))
  const [treasuryAccount, setTreasuryAccount] = useState<{value: string, valid: boolean, object: any}>({value: '', valid: false, object: null})
  const [note, setNote] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const depositAmount = contract?.security_deposit?.amount ?? 0
  const currency = contract?.template?.currency?.code ?? 'CHF'
  const parsedRetained = parseFloat(retainedAmount) || 0
  const computedReturn = Math.max(0, depositAmount - parsedRetained)

  const resetForm = () => {
    setRetainedAmount('0')
    setDate(new Date().toISOString().substring(0, 10))
    setTreasuryAccount({value: '', valid: false, object: null})
    setNote('')
  }

  const validateForm = (): boolean => {
    let valid = true

    if (!date) {
      legendInvalid('legend-deposit-return-date-required')
      valid = false
    } else {
      legendValid('legend-deposit-return-date-required')
    }

    if (!treasuryAccount.valid) {
      legendInvalid('legend-treasury-account-required')
      valid = false
    }

    if (isNaN(parsedRetained) || parsedRetained < 0 || parsedRetained > depositAmount) {
      legendInvalid('legend-retained-amount-valid')
      valid = false
    } else {
      legendValid('legend-retained-amount-valid')
    }

    return valid
  }

  const submitReturnDeposit = async () => {
    if (!companyForUser || !contract || !validateForm()) return

    setLoading(true)

    try {
      const data: any = {
        date: date,
        id_treasury_account: treasuryAccount.value,
      }
      if (parsedRetained > 0) data.retained_amount = parsedRetained
      if (note) data.note = note

      const response = await RecurringContractDA.returnDeposit(companyForUser.id, contract.id, data)

      if (response.status === 200) {
        setContract(response.data)
        resetForm()
        modalHide('modal-deposit-return')
        GlobalSwal.fire({
          title: lang.labels.depositReturnedSuccess,
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        })
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div id="modal-deposit-return" className="modal fade" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered mw-650px">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="fw-bolder">{lang.labels.returnDeposit}</h2>
            <div className="btn btn-icon btn-sm btn-active-icon-primary" data-bs-dismiss="modal">
              <span className="svg-icon svg-icon-1">
                <i className="bi bi-x-lg"></i>
              </span>
            </div>
          </div>
          <div className="modal-body scroll-y mx-5 my-3">
            <div className="d-flex justify-content-between align-items-center bg-light-primary rounded p-4 mb-5">
              <span className="fw-bold text-muted">{lang.labels.depositAmount}</span>
              <span className="fw-bolder fs-4" style={{fontVariantNumeric: 'tabular-nums'}}>{currency} {formatMoney(depositAmount)}</span>
            </div>
            <div className="mb-5">
              <label className="form-label">{lang.labels.retainedAmount} ({currency})</label>
              <input
                className="form-control"
                type="number"
                step="0.01"
                min="0"
                max={depositAmount}
                placeholder="0.00"
                value={retainedAmount}
                onChange={(e) => setRetainedAmount(e.target.value)}
                style={{fontVariantNumeric: 'tabular-nums'}}
              />
              <div className="form-text text-muted fs-8">{lang.labels.retainedAmountHint}</div>
              <AppLegend component={undefined} attribute={{check: !isNaN(parsedRetained) && parsedRetained >= 0 && parsedRetained <= depositAmount, name: "retained-amount", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}} />
            </div>
            <div className="d-flex justify-content-between align-items-center border border-dashed border-primary rounded p-4 mb-5">
              <span className="fw-bold">{lang.labels.amountToReturn}</span>
              <span className="fw-bolder fs-3 text-primary" style={{fontVariantNumeric: 'tabular-nums'}}>{currency} {formatMoney(computedReturn)}</span>
            </div>
            <div className="mb-5">
              <label className="form-label required">{lang.labels.date}</label>
              <input
                className="form-control"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              <AppLegend component={undefined} attribute={{check: !!date, name: "deposit-return-date", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}} />
            </div>
            <div className="mb-5">
              <label className="form-label required">{lang.labels.treasuryAccount}</label>
              <AppInputTreasuryAccount
                solid={false}
                treasuryAccountsForCompany={treasuryAccountsForCompany}
                treasuryAccount={treasuryAccount}
                setTreasuryAccount={setTreasuryAccount}
              />
            </div>
            <div className="mb-5">
              <label className="form-label">{lang.labels.depositNote}</label>
              <textarea
                className="form-control"
                rows={3}
                placeholder={lang.labels.depositNote}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-light" type="button" data-bs-dismiss="modal" onClick={resetForm}>{lang.labels.cancel}</button>
            <button className="btn btn-primary" type="button" disabled={loading} onClick={submitReturnDeposit}>
              {loading ? lang.labels.pleaseWait : lang.labels.returnDeposit}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalDepositReturn;
